import { useState } from "react";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
  "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const PIN_REGEX = /^\d{6}$/;

export const validateStep3 = (formData) => {
  const errors = {};
  if (!formData.address_line1?.trim()) errors.address_line1 = "Required";
  if (!formData.pincode) errors.pincode = "Required";
  else if (!PIN_REGEX.test(formData.pincode)) errors.pincode = "Enter 6-digit pincode";
  if (!formData.city?.trim()) errors.city = "Required";
  if (!formData.state) errors.state = "Required";
  return errors;
};

export default function Step3Address({ formData, setFormData, errors }) {
  const [pinLoading, setPinLoading] = useState(false);
  const [pinTagInput, setPinTagInput] = useState("");
  const [pinTagError, setPinTagError] = useState("");

  const set = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const lookupPincode = async () => {
    const pin = formData.pincode;
    if (!pin || !PIN_REGEX.test(pin)) return;
    setPinLoading(true);
    try {
      const token = localStorage.getItem("sendtoo_token");
      const headers = { Authorization: `Bearer ${token}` };
      const endpoints = [
        `${API}/api/v2/pincodes/${pin}`,
        `${API}/api/v1/pincodes/${pin}`,
        `${API}/api/v2/pincode-master/${pin}`,
      ];
      for (const url of endpoints) {
        try {
          const r = await fetch(url, { headers });
          if (!r.ok) continue;
          const d = await r.json();
          const city = d.city || d.district || d.taluk;
          const state = d.state || d.state_name;
          if (city || state) {
            setFormData((p) => ({
              ...p,
              city: city || p.city,
              state: state || p.state,
            }));
            break;
          }
        } catch {
          // try next endpoint
        }
      }
    } finally {
      setPinLoading(false);
    }
  };

  const addPincodes = (raw) => {
    const value = (raw || pinTagInput).trim();
    if (!value) return;
    const parts = value
      .split(/[,\s\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const invalid = parts.filter((p) => !PIN_REGEX.test(p));
    if (invalid.length) {
      setPinTagError(`Invalid: ${invalid.slice(0, 3).join(", ")}`);
      // still add valid ones
    } else {
      setPinTagError("");
    }
    const valid = parts.filter((p) => PIN_REGEX.test(p));
    const current = formData.service_area_pincodes || [];
    const merged = Array.from(new Set([...current, ...valid]));
    setFormData((p) => ({ ...p, service_area_pincodes: merged }));
    setPinTagInput("");
  };

  const removePincode = (pin) => {
    setFormData((p) => ({
      ...p,
      service_area_pincodes: (p.service_area_pincodes || []).filter((x) => x !== pin),
    }));
  };

  const handleTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addPincodes(pinTagInput);
    } else if (e.key === "Backspace" && !pinTagInput && (formData.service_area_pincodes || []).length) {
      const arr = [...formData.service_area_pincodes];
      arr.pop();
      setFormData((p) => ({ ...p, service_area_pincodes: arr }));
    }
  };

  const pincodes = formData.service_area_pincodes || [];

  return (
    <div className="space-y-7" data-testid="step3-address">
      <div>
        <h2 className="text-lg font-bold text-slate-800">Address & Service Area</h2>
        <p className="text-sm text-slate-500">
          Where is the partner based and where do they operate?
        </p>
      </div>

      {/* Registered Address */}
      <Section title="Registered Address">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Address Line 1" required error={errors.address_line1} testid="addr1">
            <Input
              value={formData.address_line1 || ""}
              onChange={(e) => set("address_line1", e.target.value)}
              placeholder="Plot / Building / Street"
              data-testid="addr1-input"
            />
          </Field>
          <Field label="Address Line 2" testid="addr2">
            <Input
              value={formData.address_line2 || ""}
              onChange={(e) => set("address_line2", e.target.value)}
              placeholder="Area / Landmark"
              data-testid="addr2-input"
            />
          </Field>
          <Field
            label="Pincode"
            required
            error={errors.pincode}
            helper={pinLoading ? "Looking up city/state…" : "We auto-fill city & state on blur"}
            testid="pincode"
          >
            <Input
              maxLength={6}
              value={formData.pincode || ""}
              onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))}
              onBlur={lookupPincode}
              placeholder="530016"
              data-testid="pincode-input"
            />
          </Field>
          <Field label="City" required error={errors.city} testid="city">
            <Input
              value={formData.city || ""}
              onChange={(e) => set("city", e.target.value)}
              placeholder="Visakhapatnam"
              data-testid="city-input"
            />
          </Field>
          <Field label="State" required error={errors.state} testid="state">
            <select
              value={formData.state || ""}
              onChange={(e) => set("state", e.target.value)}
              className="w-full border border-slate-300 rounded h-10 px-2 text-sm bg-white"
              data-testid="state-select"
            >
              <option value="">Select state…</option>
              {INDIAN_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Section>

      {/* Google Maps Location */}
      <Section title="Google Maps Location">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Latitude" testid="lat">
            <Input
              type="number"
              step="0.000001"
              value={formData.latitude ?? ""}
              onChange={(e) =>
                set("latitude", e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="17.686816"
              data-testid="lat-input"
            />
          </Field>
          <Field label="Longitude" testid="lng">
            <Input
              type="number"
              step="0.000001"
              value={formData.longitude ?? ""}
              onChange={(e) =>
                set("longitude", e.target.value === "" ? "" : Number(e.target.value))
              }
              placeholder="83.218482"
              data-testid="lng-input"
            />
          </Field>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Open Google Maps → right-click your location → click the coordinates shown → paste here
        </p>
        <div
          className="mt-2 border border-dashed border-slate-300 rounded px-3 py-2 text-sm bg-slate-50"
          data-testid="coords-preview"
        >
          {formData.latitude !== "" &&
          formData.latitude !== undefined &&
          formData.longitude !== "" &&
          formData.longitude !== undefined ? (
            <span className="text-slate-700">
              <span className="mr-1">📍</span>
              {Number(formData.latitude).toFixed(6)}, {Number(formData.longitude).toFixed(6)}
            </span>
          ) : (
            <span className="text-slate-400 italic">Coordinates not yet entered</span>
          )}
        </div>
      </Section>

      {/* Service Area Pincodes */}
      <Section title="Service Area Pincodes">
        <div className="border border-slate-300 rounded p-2 flex flex-wrap gap-1.5 min-h-[46px] bg-white">
          {pincodes.map((p) => (
            <span
              key={p}
              className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium"
              data-testid={`pin-tag-${p}`}
            >
              {p}
              <button
                type="button"
                onClick={() => removePincode(p)}
                className="hover:bg-emerald-700 rounded-full p-0.5"
                aria-label={`Remove ${p}`}
                data-testid={`pin-tag-remove-${p}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <input
            value={pinTagInput}
            onChange={(e) => setPinTagInput(e.target.value)}
            onKeyDown={handleTagKey}
            onBlur={() => pinTagInput && addPincodes(pinTagInput)}
            onPaste={(e) => {
              const text = e.clipboardData.getData("text");
              if (text.includes(",") || text.includes("\n")) {
                e.preventDefault();
                addPincodes(text);
              }
            }}
            className="flex-1 min-w-[120px] outline-none text-sm px-1"
            placeholder={pincodes.length ? "Type or paste…" : "Type pincode + Enter / paste comma-separated"}
            data-testid="pin-tag-input"
          />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-[11px] text-slate-500">
            Used for route assignment and quotes
          </p>
          <span
            className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
            data-testid="pin-count-badge"
          >
            {pincodes.length} pincode{pincodes.length === 1 ? "" : "s"} added
          </span>
        </div>
        {pinTagError && (
          <p className="text-[11px] text-red-600 mt-1">{pinTagError}</p>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, required, helper, error, children, testid }) {
  return (
    <div data-testid={`field-${testid}`}>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
      {helper && !error && (
        <p className="text-[11px] text-slate-400 mt-1">{helper}</p>
      )}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}
